/* eslint-disable import/extensions */
import {type IProject} from '@unbrs/plastic-data/src/brs';
import * as entities from 'entities';
import {getProject} from '../../feeds';
import {BrsBaseWidget} from '../base';
import htmllistitem from './views/project-list-item.ktmpl.html';

export type BrsPlasticProjectWidgetOptions = {
	project: IProject | string;
	template?: string;
	resourceUrl?: string;
};

export class BrsPlasticProjectWidget extends BrsBaseWidget {
	private readonly template: string;
	constructor(selector: Element, options: BrsPlasticProjectWidgetOptions) {
		super(selector, options);
		this.template = options.template ?? htmllistitem;
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.initialize(options);
	}

	async initialize(options: BrsPlasticProjectWidgetOptions) {
		const template = kendo.template(this.template);
		const project = typeof options.project === 'string' ? (await getProject(options.project, options.resourceUrl ?? '')) : options.project;

		if (project === undefined) {
			console.error('Project not found');
			return;
		}

		$(this.element).html(template(
			{
				project: new ProjectView(project),
			},
		));
	}
}

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface JQuery {
		kendoBrsPlasticProjectWidget(options: BrsPlasticProjectWidgetOptions): JQuery;
		data(widgetName: 'kendoBrsPlasticProjectWidget'): BrsPlasticProjectWidget;
	}
}

BrsBaseWidget.registerBrsWidget(
	BrsPlasticProjectWidget,
	'BrsPlasticProjectWidget',
);

const FIELDS: Array<{name: string; title: string; suffix?: string; preprocess?: (x: any) => string}> = [
	{
		name: 'budget',
		title: 'Budget',
	},
	{
		name: 'leadUnit',
		title: 'Lead unit',
	},
	{
		name: 'brs_objectives',
		title: 'Objectives',
	},
	{
		name: 'brs_activities',
		title: 'Activities',
	},
	{
		name: 'focusArea',
		title: 'Focus areas',
	},
	{
		name: 'pdo',
		title: 'Development Objective',
	},
	{
		name: 'keywords',
		title: 'Keywords',
	},
];

class ProjectView {
	private readonly _project: IProject;
	constructor(public project: IProject) {
		this._project = project;
	}

	get id() {
		return this._project.id;
	}

	get feed() {
		return this._project.type;
	}

	get name() {
		return this._project.name;
	}

	get description() {
		return entities.decodeHTML(this._project.description ?? '');
	}

	get budget() {
		return this._project.budget ? new Intl.NumberFormat('en-US', {style: 'decimal'}).format(this._project.budget) + ' USD' : '';
	}

	get sourceDate() {
		return this._project.sourceDate.toLocaleDateString();
	}

	get dates() {
		let start = this._project.startDate;
		let end = this._project.endDate;

		start &&= new Date(start);
		end &&= new Date(end);

		if (start && end) {
			return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
		}

		if (start) {
			return `starts on ${start.toLocaleDateString()}`;
		}

		return 'no dates';
	}

	get status() {
		switch (this._project.status) {
			case 'not_started': {
				return 'not started';
			}

			case 'on_going': {
				return 'on going';
			}

			case 'completed': {
				return 'completed';
			}

			// eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
			default: {
				return 'Unknown';
			}
		}
	}

	get beneficiaries() {
		return this._project.beneficiaries.map(b => b.name).join(', ');
	}

	get unRegions() {
		if (this._project.unRegions) {
			return this._project.unRegions.filter(r => r !== 'other').join(', ');
		}

		return '';
	}

	get feedRegions() {
		if (this._project.feedRegions) {
			return this._project.feedRegions.join(', ');
		}

		return '';
	}

	get implementedBy() {
		if (!this._project.implementedBy) {
			return undefined;
		}

		return this._project.implementedBy.map(b => {
			if (b.url) {
				return `<a href="${b.url}">${b.name}</a>`;
			}

			return b.name;
		}).join(', ');
	}

	get donors() {
		if (!this._project.donors) {
			return undefined;
		}

		return this._project.donors.map(b => {
			if (b.url) {
				return `<a href="${b.url}">${b.name}</a>`;
			}

			return b.name;
		}).join(', ');
	}

	get keywords() {
		if (!this._project.keywords) {
			return undefined;
		}

		return this._project.keywords.map(b => b.name).join(', ');
	}

	get umbrella() {
		if (this._project.isUmbrella === true) {
			return '✅';
		}

		return '';
	}

	get url() {
		return this._project.url;
	}

	get umbrellaName() {
		return this._project.umbrellaName;
	}

	get umbrellaUrl() {
		if (!this._project.isUmbrella && this._project.umbrellaName) {
			return this._project.umbrellaUrl ?? '#/project/' + this._project.umbrellaId;
		}

		return undefined;
	}

	get list() {
		const result: Array<{name: string; value: string}> = [];
		const project = this._project as unknown as Record<string, any>;
		const self = this as unknown as Record<string, any>;
		for (const field of FIELDS) {
			const descriptor = Object.getOwnPropertyDescriptor(ProjectView.prototype, field.name);

			if (descriptor && typeof descriptor.get === 'function') {
				const value = descriptor.get.call(self) as string;
				if (value) {
					result.push({
						name: field.title,
						value,
					});
				}

				continue;
			}

			if (project[field.name]) {
				let value = '';
				switch (typeof project[field.name]) {
					case 'number': {
						value = (project[field.name] as number).toString();
						break;
					}

					case 'object': {
						value = (project[field.name] as string[]).join(', ');
						break;
					}

					default: {
						value = project[field.name] as string;
						break;
					}
				}

				if (field.suffix) {
					value += field.suffix;
				}

				result.push({
					name: field.title,
					value: entities.decodeHTML(value),
				});
			}
		}

		return result;
	}
}
